import { Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { CustomEmailGroup, CustomEmailKind, ICustomEmailState } from '@common-interfaces/custom-email';
import { SectionCardComponent } from '../../../../../components/shared/section-card/section-card.component';

const ICONS: Record<CustomEmailKind, string> = {
    NEW_BOOKING: 'ph-calendar-check',
    CHANGED_BOOKING: 'ph-calendar-dots',
    CANCELED_BOOKING: 'ph-calendar-x',
    BOOKING_REMINDER: 'ph-bell-ringing',
    BOOKING_PAYMENT_REMINDER: 'ph-credit-card',
    CUSTOMER_FEEDBACK: 'ph-chat-circle-text',
    BIRTHDAY: 'ph-cake',
    GIFT_CARD: 'ph-gift',
    HEALTH_BENEFIT: 'ph-heartbeat'
};

const GROUP_ORDER: CustomEmailGroup[] = ['BOOKINGS', 'REMINDERS', 'AFTER_VISIT', 'GIFT_CARDS'];

/** The built-in customer emails, each sent either as Funbutler's standard email or with the venue's own design. */
@Component({
    selector: 'booking-emails',
    imports: [FormsModule, TranslatePipe, ButtonModule, InputTextModule, SkeletonModule, SectionCardComponent],
    templateUrl: './booking-emails.html'
})
export class BookingEmailsComponent {
    emails = input.required<ICustomEmailState[]>();
    loading = input(false);
    /** The kind currently being saved, to show progress on that row only. */
    busyKind = input<CustomEmailKind | null>(null);
    /** Custom templates need the Maillune editor, which is enabled per organisation. */
    editorAvailable = input(true);

    useCustom = output<ICustomEmailState>();
    useDefault = output<ICustomEmailState>();
    editTemplate = output<ICustomEmailState>();
    subjectChanged = output<{ email: ICustomEmailState; subject: string }>();

    modeOptions = [
        { value: 'DEFAULT', label: 'BOOKING_EMAIL_MODE_DEFAULT' },
        { value: 'CUSTOM', label: 'BOOKING_EMAIL_MODE_CUSTOM' }
    ];

    /** Emails in their sections, in a fixed order. */
    groups = computed(() =>
        GROUP_ORDER.map((group) => ({ group, emails: this.emails().filter((email) => email.group === group) })).filter((g) => g.emails.length)
    );

    /** The row whose subject is being edited, and the draft text. */
    editingSubject = signal<CustomEmailKind | null>(null);
    subjectDraft = signal('');

    icon(kind: CustomEmailKind): string {
        return ICONS[kind];
    }

    subject(email: ICustomEmailState): string {
        return email.template?.subject || email.defaultSubject;
    }

    thumbnail(email: ICustomEmailState): string | null {
        return email.template?.thumbnailImageData ?? null;
    }

    modeChanged(email: ICustomEmailState, mode: 'DEFAULT' | 'CUSTOM' | null): void {
        if (!mode || mode === email.mode) {
            return;
        }
        if (mode === 'CUSTOM') {
            this.useCustom.emit(email);
        } else {
            this.useDefault.emit(email);
        }
    }

    startSubjectEdit(email: ICustomEmailState): void {
        this.subjectDraft.set(this.subject(email));
        this.editingSubject.set(email.kind);
    }

    saveSubject(email: ICustomEmailState): void {
        const subject = this.subjectDraft().trim();
        this.editingSubject.set(null);
        if (subject && subject !== this.subject(email)) {
            this.subjectChanged.emit({ email, subject });
        }
    }
}
